import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useBusinessInfo } from "@/api/queries";
import { useAuthMe } from "@/api/queries/auth";
import { useTenantBranding } from "@/api/queries/branding";
import { quoteShortId } from "@/components/QuoteIdBadge";
import { PageHero } from "@/components/ui/PageHero";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { apiFetch, ApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { resolveShell } from "@/shell/resolveShell";

/**
 * Public contact form at ``/{lang}/contact``.
 *
 * Pre-sales enquiries land here. The submission is persisted +
 * forwarded to the support inbox; failure to deliver e-mail still
 * results in a 201 because the BE has the row (admin can read out
 * of band).
 *
 * ``org_slug`` names the tenant the enquiry belongs to — same as the
 * landing page's ``<ContactForm>``. Without it the BE falls back to the
 * request's tenant context, and on an apex / default-fallback host
 * (every localhost run) that fallback is skipped on purpose: the row
 * lands in the platform inbox (``org_id = NULL``), which the tenant
 * admin's "Zprávy z kontaktu" board — scoped to its own ``org_id`` —
 * can never show. This page renders tenant branding, so its messages
 * belong to that tenant.
 *
 * Admin links into /contact with ?topic=billing-grant|enterprise|
 * plan-change&tier=X — the subject prefills from the topic so the
 * support inbox knows which flow this came from instead of getting
 * a generic ticket with no context.
 *
 * A stopped quote links here with ?topic=quote&ref=<quote slug> (KAN-1234).
 * KAN-1650 — the slug travels to the BE as ``quote_ref``: the message then
 * LINKS the part (file, configuration, block reason) instead of the shop
 * having to ask for the drawing. The subject shows the customer's public
 * part ID (``Q-XXXXX``), never the opaque slug. KAN-2073 — with
 * ``&message=`` the stop card's "let the workshop fix it" exit prefills the
 * message with the finding that stopped the part.
 *
 * KAN-1954 — on the platform host the page speaks to a shop owner
 * weighing Forgmatic, not to a shop's customer, and asks for a topic.
 * The BE has no topic column, so the topic label rides at the front of
 * the subject; the inbox reads it without a migration.
 */

/** Topics offered on the platform host, in display order. */
const PLATFORM_TOPICS = ["sales", "support", "billing", "other"] as const;
type PlatformTopic = (typeof PLATFORM_TOPICS)[number];

/** The admin and pricing links name their flow; map it to a topic. */
function prefillTopic(topic: string | null): PlatformTopic | "" {
  if (topic === "enterprise" || topic === "plan-change") return "sales";
  if (topic === "billing-grant") return "billing";
  return "";
}

function prefillSubject(
  topic: string | null,
  tier: string | null,
  ref: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (topic === "quote" && ref) {
    return t("topicPrefill.quote", { ref: quoteShortId(ref) });
  }
  if (topic === "billing-grant") return t("topicPrefill.billingGrant");
  if (topic === "enterprise") return t("topicPrefill.enterprise");
  if (topic === "plan-change") {
    return tier
      ? t("topicPrefill.planChange", { tier })
      : t("topicPrefill.planChangeNoTier");
  }
  return "";
}

interface ContactResponse {
  id: number;
}

export default function Contact(): JSX.Element {
  const { t } = useTranslation("contact");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const [searchParams] = useSearchParams();
  const branding = useTenantBranding();
  const orgSlug = branding.data?.slug ?? null;
  // KAN-1935 — tentýž kontaktní e-mail jako v patičce; pošta na něj
  // přistane ve Zprávách z kontaktu stejně jako tenhle formulář. Jen
  // u vyplněné identity (výplň nese ``orders@example.com``).
  const business = useBusinessInfo();
  const contactEmail = business.data?.legal_name
    ? business.data.support_email
    : "";
  const isPlatform = resolveShell() === "platform";

  useSeo({
    title: t("seo.title"),
    description: t(isPlatform ? "seo.descriptionPlatform" : "seo.description"),
    path: "/contact",
    lang,
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(() =>
    prefillSubject(
      searchParams.get("topic"),
      searchParams.get("tier"),
      searchParams.get("ref"),
      t,
    ),
  );
  // KAN-2073 — „Upravit ručně dílnou" z bloku „Co s tím" posílá důvod
  // (nález na dílu) jako předvyplněnou zprávu; zákazník ji může upravit.
  const [message, setMessage] = useState(() =>
    searchParams.get("topic") === "quote"
      ? (searchParams.get("message") ?? "").slice(0, 4000)
      : "",
  );
  const [topic, setTopic] = useState<PlatformTopic | "">(() =>
    prefillTopic(searchParams.get("topic")),
  );
  const quoteRef =
    searchParams.get("topic") === "quote" ? searchParams.get("ref") : null;
  // KAN-1650 — přihlášenému zákazníkovi jméno a e-mail doplníme; co už
  // napsal, nepřepisujeme.
  const me = useAuthMe({ retry: false });
  useEffect(() => {
    const user = me.data;
    // Anonymní zástupný záznam e-mail nemá — pak není co doplnit.
    if (!user?.email) return;
    const fullName = [user.first_name, user.last_name]
      .filter(Boolean)
      .join(" ");
    setName((prev) => prev || fullName);
    setEmail((prev) => prev || user.email);
  }, [me.data]);
  const [loading, setLoading] = useState(false);
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await apiFetch<ContactResponse>("/api/v1/contact", {
        method: "POST",
        body: {
          name: name.trim(),
          email: email.trim(),
          subject: (isPlatform && topic
            ? `${t(`topic.${topic}`)}: ${subject.trim()}`
            : subject.trim()
          ).slice(0, 200),
          message: message.trim(),
          ...(orgSlug ? { org_slug: orgSlug } : {}),
          ...(quoteRef ? { quote_ref: quoteRef } : {}),
        },
      });
      setSubmittedId(result.id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t("error.rateLimited"));
      } else if (err instanceof ApiError && err.code === "plan_monthly_limit_reached") {
        // KAN-1674 — obchod vyčerpal měsíční strop poptávek; text staví ApiError.
        setError(err.message);
      } else {
        setError(t("error.generic"));
      }
    } finally {
      setLoading(false);
    }
  }

  if (submittedId !== null) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-8">
        <div
          className="mt-16 rounded-lg border border-ok-border bg-ok-bg p-8 text-center"
          data-testid="contact-success"
        >
          <h1 className="text-2xl font-bold text-ok">
            {t("success.heading")}
          </h1>
          <p className="mt-2 text-ok">{t("success.lead")}</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <PageHero innerClassName="st-page-hero max-w-2xl px-6 pb-8 pt-10 md:pt-12">
        <h1 className="st-h2 text-3xl font-semibold tracking-tight text-ink">
          {t("heading")}
        </h1>
        <p className="st-sub mt-2 text-ink-2">{t("lead")}</p>
        {contactEmail && (
          <p className="mt-2 text-sm text-ink-2" data-testid="contact-direct-email">
            {t("directEmail")}{" "}
            <a
              href={`mailto:${contactEmail}`}
              className="font-medium text-ink underline underline-offset-2"
            >
              {contactEmail}
            </a>
          </p>
        )}
      </PageHero>

      <main className="mx-auto max-w-2xl px-6 pb-16 pt-8">

      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded-lg border border-border bg-surface p-6"
        data-testid="contact-form"
      >
        <div>
          <label
            htmlFor="contact-name"
            className="block text-sm font-medium text-ink-2"
          >
            {t("field.name")}
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={128}
            className="mt-1 w-full rounded-md border border-border-strong px-3 py-2 text-sm shadow-sm focus:border-border-strong focus:outline-none focus:ring-1 focus:ring-border-strong"
            data-testid="contact-name"
          />
        </div>
        <div>
          <label
            htmlFor="contact-email"
            className="block text-sm font-medium text-ink-2"
          >
            {t("field.email")}
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-border-strong px-3 py-2 text-sm shadow-sm focus:border-border-strong focus:outline-none focus:ring-1 focus:ring-border-strong"
            data-testid="contact-email"
          />
        </div>
        {isPlatform && (
          <div>
            <label
              htmlFor="contact-topic"
              className="block text-sm font-medium text-ink-2"
            >
              {t("field.topic")}
            </label>
            <select
              id="contact-topic"
              name="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value as PlatformTopic | "")}
              required
              className="mt-1 w-full rounded-md border border-border-strong bg-bg px-3 py-2 text-sm shadow-sm focus:border-border-strong focus:outline-none focus:ring-1 focus:ring-border-strong"
              data-testid="contact-topic"
            >
              <option value="" disabled>
                {t("topic.placeholder")}
              </option>
              {PLATFORM_TOPICS.map((key) => (
                <option key={key} value={key}>
                  {t(`topic.${key}`)}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label
            htmlFor="contact-subject"
            className="block text-sm font-medium text-ink-2"
          >
            {t("field.subject")}
          </label>
          <input
            id="contact-subject"
            name="subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            maxLength={200}
            className="mt-1 w-full rounded-md border border-border-strong px-3 py-2 text-sm shadow-sm focus:border-border-strong focus:outline-none focus:ring-1 focus:ring-border-strong"
            data-testid="contact-subject"
          />
        </div>
        <div>
          <label
            htmlFor="contact-message"
            className="block text-sm font-medium text-ink-2"
          >
            {t("field.message")}
          </label>
          <textarea
            id="contact-message"
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={6}
            maxLength={4000}
            className="mt-1 w-full rounded-md border border-border-strong px-3 py-2 text-sm shadow-sm focus:border-border-strong focus:outline-none focus:ring-1 focus:ring-border-strong"
            data-testid="contact-message"
          />
        </div>
        <button
          type="submit"
          disabled={
            loading ||
            // Submitting before branding settles would send no
            // ``org_slug`` and silently file the enquiry under the
            // platform inbox instead of this tenant's.
            branding.isPending ||
            !name ||
            !email ||
            !subject ||
            !message ||
            (isPlatform && !topic)
          }
          className="st-btn rounded-md bg-ink px-4 py-2 text-sm font-medium text-bg shadow-sm hover:bg-ink disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="contact-submit"
        >
          {loading ? t("submitting") : t("submit")}
        </button>
        <p className="text-xs text-ink-3" data-testid="contact-privacy-note">
          {t("privacy.note")}{" "}
          <Link
            to={`/${lang}/privacy`}
            className="underline underline-offset-2 hover:text-ink"
          >
            {t("privacy.link")}
          </Link>
        </p>
        {error && (
          <p className="text-sm text-danger" data-testid="contact-error">
            {error}
          </p>
        )}
      </form>
      </main>
    </>
  );
}
